/**
 * CLE-2820 — the procedures sent with an eligibility check, for the AI second opinion's context.
 *
 * Read from the same form state the order saves into `serviceDetails.procedures`. Shaped to always
 * pass the API's validation (code 1–10 characters, description at most 200, at most 50 entries,
 * amount a whole number of cents from 0 to the cap), so sending them can never make an eligibility
 * check fail: anything that would not fit is dropped or trimmed rather than sent. Nothing selected
 * means nothing is sent.
 *
 * CLE-2816 — each code also carries the order's FACILITY charge for it (`amountInCents`, summed
 * over the code's facility rows, with `specialty: "FACILITY"`), when the form has one. Some UHC
 * plans list a copay per procedure family; the API quotes the item whose codes carry the largest
 * facility total. A code with no facility row is sent without an amount.
 */

export interface IEligibilityProcedure {
  code: string;
  description?: string;
  amountInCents?: number;
  specialty?: string;
}

export const ELIGIBILITY_PROCEDURES_MAX = 50;
const CODE_MAX_LENGTH = 10;
const DESCRIPTION_MAX_LENGTH = 200;
/** The API's cap (`ELIGIBILITY_PROCEDURE_AMOUNT_MAX_CENTS`): $100,000,000. */
export const ELIGIBILITY_PROCEDURE_AMOUNT_MAX_CENTS = 10_000_000_000;
export const FACILITY_SPECIALTY = "FACILITY";

const codeOf = (procedure: any): string =>
  String(procedure?.code ?? procedure?.cptCode ?? "").trim();

/** The summed FACILITY `amountInCents` per code, over every row of the form. */
const facilityCentsByCode = (serviceDetails: any[]): Map<string, number> => {
  const totals = new Map<string, number>();
  serviceDetails.forEach((procedure: any) => {
    const code = codeOf(procedure);
    const amount = procedure?.amountInCents;
    if (!code || procedure?.specialty !== FACILITY_SPECIALTY) return;
    if (typeof amount !== "number" || !Number.isFinite(amount) || amount < 0)
      return;
    totals.set(code, (totals.get(code) ?? 0) + amount);
  });
  return totals;
};

export const buildEligibilityProcedures = (
  serviceDetails: unknown
): IEligibilityProcedure[] => {
  if (!Array.isArray(serviceDetails)) return [];
  const facilityCents = facilityCentsByCode(serviceDetails);
  const seen = new Set<string>();
  const procedures: IEligibilityProcedure[] = [];
  serviceDetails.forEach((procedure: any) => {
    const code = codeOf(procedure);
    if (!code || code.length > CODE_MAX_LENGTH || seen.has(code)) return;
    if (procedures.length >= ELIGIBILITY_PROCEDURES_MAX) return;
    seen.add(code);
    const rawDescription = procedure?.cptDescription ?? procedure?.description;
    const description =
      typeof rawDescription === "string"
        ? rawDescription.trim().slice(0, DESCRIPTION_MAX_LENGTH)
        : "";
    const total = facilityCents.get(code);
    const amountInCents = total === undefined ? undefined : Math.round(total);
    const facility =
      amountInCents !== undefined &&
      amountInCents <= ELIGIBILITY_PROCEDURE_AMOUNT_MAX_CENTS
        ? { amountInCents, specialty: FACILITY_SPECIALTY }
        : {};
    procedures.push({
      code,
      ...(description ? { description } : {}),
      ...facility,
    });
  });
  return procedures;
};
